import { clamp, hash01 } from '@/core/rng';

/**
 * Death (Design III §139, and the keystone `ROADMAP.md` §14 names).
 *
 * Nothing in this world died. `tools/graves.ts 25` measured it: twenty-five
 * years, one band becoming nine, three hundred souls, **zero deaths**. The
 * only paths that reduced a population were starvation and thirst, neither of
 * which has ever fired, so `reconcilePeople` — which already removes somebody
 * and names them — was never asked to. The consequence reached much further
 * than a missing graveyard: every ecological trajectory Design III asks for,
 * from succession to lineages to *the last old cedar*, is a claim about things
 * ending, and a world where nothing ends cannot hold one.
 *
 * ## Why a rate per age, and not a death rate
 *
 * A single per-capita death rate would kill infants and elders at the same
 * pace, which gets the *count* right and the *world* wrong: it produces no
 * elders, no orphans, no lineages, and no sense that surviving childhood
 * meant something. Real mortality is U-shaped and enormously so — a forager's
 * first year is more dangerous than their fortieth.
 *
 * So this is a **Siler competing-hazards model**, the standard shape for
 * exactly this population:
 *
 *     h(a) = A·exp(−B·a)   +   C   +   D·exp(G·a)
 *            ── juvenile ──   const   ── senescent ──
 *
 * The first term is the steep infant risk decaying through childhood; the
 * second is the flat background of accident, violence and infection that does
 * not care how old you are; the third is senescence, negligible until midlife
 * and then doubling every decade or so.
 *
 * The constants are fitted to give the demography anthropology actually
 * reports for hunter-gatherers, and `tools/lifetable.ts` prints what they
 * produce so the claim can be checked rather than believed: roughly **four in
 * ten dead before fifteen**, a life expectancy at birth in the low thirties,
 * and — the number that matters for whether a world has elders — those who
 * reach fifteen expecting **another three and a half decades**, with a real
 * tail into the seventies.
 *
 * Purity: a pure function of age and condition. Who actually dies is drawn in
 * `died()` from the person's own identity and the day, never from a clock or
 * a counter, so the same world kills the same people on the same days.
 */

/** Siler parameters. Change none of these without re-running `tools/lifetable.ts`. */
export const A = 0.238, B = 0.70;    // juvenile hazard, and how fast it decays
export const C = 0.0115;             // the flat background of a dangerous life
export const D = 0.00012, G = 0.0875; // senescence, and its doubling rate

/** Annual hazard of death at an age, before condition. */
export function hazardPerYear(ageYears: number): number {
  const a = Math.max(0, ageYears);
  return A * Math.exp(-B * a) + C + D * Math.exp(G * a);
}

/**
 * How much a person's own condition moves their hazard.
 *
 * `vitality` is the 0..1 the roster already carries and already uses to decide
 * who goes first under starvation. A frail person is likelier to die of
 * anything; a strong one is not immortal. Deliberately mild — a factor of
 * about two across the whole range — because the point of the age term is
 * that *age* dominates, and a model where vitality dominates is a health bar.
 */
export const frailty = (vitality: number): number =>
  1.45 - 0.9 * clamp(vitality, 0, 1);

/** Annual hazard for a particular person. */
export const hazardFor = (ageYears: number, vitality: number): number =>
  hazardPerYear(ageYears) * frailty(vitality);

/**
 * Did this person die today?
 *
 * The draw is a hash of who they are and which day it is — no clock, no
 * counter, no dependence on how the world was advanced — so `advance(a,c)` and
 * `advance(a,b)+advance(b,c)` bury exactly the same people, which
 * `test/simulation-catchup.ts` requires of everything here.
 *
 * `dayIndex` must be the absolute day number, not a day-of-year.
 */
export function died(
  personSeed: number, dayIndex: number, ageYears: number, vitality: number,
): boolean {
  const annual = hazardFor(ageYears, vitality);
  // convert the annual hazard to one day's probability
  const daily = 1 - Math.exp(-annual / 365);
  return hash01(personSeed, dayIndex, 0x6d1e) < daily;
}

/**
 * The life table these constants imply — survivorship and remaining life
 * expectancy, integrated year by year. Used by `tools/lifetable.ts`, and by
 * `test/mortality.ts` to hold the demography to what it claims.
 */
export function lifeTable(maxAge = 110): {
  survival: number[]; expectancy: number[];
} {
  const survival: number[] = [1];
  for (let a = 0; a < maxAge; a++) {
    survival.push(survival[a] * Math.exp(-hazardPerYear(a + 0.5)));
  }
  // e(x) = Σ_{a≥x} l(a+½) / l(x), in whole years
  const expectancy: number[] = [];
  for (let x = 0; x <= maxAge; x++) {
    let sum = 0;
    for (let a = x; a < maxAge; a++) sum += (survival[a] + survival[a + 1]) / 2;
    expectancy.push(survival[x] > 1e-9 ? sum / survival[x] : 0);
  }
  return { survival, expectancy };
}
